import React from 'react';
import PropTypes from 'prop-types';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Button } from '@uhg-abyss/web/ui/Button';
import { RenderHtml } from '@sde/widgets-core/src/common/RenderHtml';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { useModal } from '@src/state/modal';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useUpdateOONConsentQuery } from '@sde/widgets-core/src/common/hooks/useUpdateOONConsentModalQuery';
import { event } from '@uhg-abyss/web/tools/event/event';

const StyledModal = styled(Modal, {
  '&.abyss-modal-header-container': { marginTop: '$md' },
  '&.abyss-modal-overlay': {
    // adding one more 9 here to be layed on top of provider drawer
    zIndex: 9999999,
  },
});

const StyledHeading = styled(Heading, {
  '&.abyss-heading-root': {
    fontFamily: 'UHCSerif,Georgia Bold,serif',
    fontWeight: '$semibold',
    fontSize: '2.027rem',
    lineHeight: '3rem',
    marginTop: '10px',
  },
});

const StyledDiv = styled('div', {
  width: '100%',
  marginTop: '15px',
});

export const OONConsentOptOutConfirmationModal = ({
  corporateProviderMpin,
  taxIdNumber,
}) => {
  const { modals } = useModal.state();
  const { toggleModal } = useModal.actions();
  const { toast } = useToast();
  const { user } = useUser.state();
  const { isTechnicalUser } = user;
  const { getMessage } = usePPDLagoon();
  const titleSubtextMessage = getMessage(
    'oon-consent-optout-confirmation-model-title-subtext'
  );
  const optoutPopupMessage = getMessage(
    'oon-consent-modal-optout-popup-message'
  );
  const optoutApiErrorPopupMessage = getMessage(
    'oon-consent-modal-optout-api-error-popup-message'
  );
  const [, updatePopup] = useUpdateOONConsentQuery({
    onCompleted: () => {
      toast.show({
        title: 'Opt out confirmed',
        message: `${optoutPopupMessage}`,
        variant: 'success',
      });
    },
    onError: () => {
      toast.show({
        title: 'Unable to opt out',
        message: optoutApiErrorPopupMessage,
        variant: 'error',
      });
    },
  });

  const handleConfirmationModalClose = () => {
    toggleModal({
      key: 'oon-consent-optout-confirmation-modal',
      isOpen: false,
    });
  };

  const handleGoBack = () => {
    event('OON_CONSENT_OPT_OUT_CANCELLATION', {
      jobName: 'paperless consent opt-out',
    });
    event('OON_CONSENT_OPT_OUT_CONFIRMATION', {
      modalName: 'paperless consent opt out confirmation modal',
    });
    toggleModal({
      key: 'oon-consent-optout-confirmation-modal',
      isOpen: false,
    });
    toggleModal({
      key: 'oon-consent-modal',
      isOpen: true,
    });
  };

  const handleOptOut = () => {
    updatePopup({
      variables: {
        corpOwnerId: corporateProviderMpin,
        taxIdNumber,
        providerEmail: '',
        convertToElectronic: false,
      },
    });
    event('OON_CONSENT_OPT_OUT_COMPLETION', {
      jobName: 'paperless consent opt-out',
    });
    event('OON_CONSENT_OPT_OUT_CONFIRMATION', {
      modalName: 'paperless consent opt out confirmation modal',
    });
    toggleModal({
      key: 'oon-consent-optout-confirmation-modal',
      isOpen: false,
    });
  };

  return (
    <React.Fragment>
      <StyledModal
        id='oon-consent-optout-confirmation-modal'
        isOpen={modals['oon-consent-optout-confirmation-modal']}
        hideClose={!isTechnicalUser}
        onClose={handleConfirmationModalClose}
        closeOnClickOutside={isTechnicalUser}
        shouldCloseOnEsc={isTechnicalUser}
      >
        <Modal.Section>
          <StyledHeading offset={1} textAlign='left'>
            Are you sure you want to opt out?
          </StyledHeading>
          <StyledDiv>
            <RenderHtml>{titleSubtextMessage}</RenderHtml>
          </StyledDiv>
        </Modal.Section>
        <Modal.Section>
          <Layout.Group alignLayout='right' alignItems='center'>
            <Button onClick={() => handleGoBack()} variant='outline'>
              No, go back
            </Button>
            <Button onClick={() => handleOptOut()}>Yes, opt out</Button>
          </Layout.Group>
        </Modal.Section>
      </StyledModal>
    </React.Fragment>
  );
};
OONConsentOptOutConfirmationModal.propTypes = {
  corporateProviderMpin: PropTypes.string.isRequired,
  taxIdNumber: PropTypes.string.isRequired,
};
